/**
 * Device detection utilities for PWA functionality
 * @module utils/deviceUtils
 * @version 4.10.0
 */

export interface DeviceInfo {
  isMobile: boolean;
  isIOS: boolean;
  isAndroid: boolean;
  isStandalone: boolean;
  canInstallPWA: boolean;
  isIOSSafari: boolean;
  isSamsungBrowser: boolean;
}

/**
 * Get comprehensive device information
 */
export const getDeviceInfo = (): DeviceInfo => {
  const userAgent = navigator.userAgent.toLowerCase();

  // Platform detection
  const isIOS = /iphone|ipad|ipod/.test(userAgent);
  const isAndroid = /android/.test(userAgent);
  const isMobile = isIOS || isAndroid || /mobile|phone/.test(userAgent);

  // iOS Safari detection (needed for manual install instructions)
  const isSafari = /safari/.test(userAgent) && !/chrome|crios|fxios/.test(userAgent);
  const isIOSSafari = isIOS && isSafari;

  // Samsung Internet detection (needed for manual install instructions when beforeinstallprompt is absent)
  const isSamsungBrowser = /samsungbrowser/i.test(userAgent);

  // Check if already running as installed PWA (standalone mode)
  const isStandalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true || // iOS Safari
    document.referrer.includes('android-app://'); // Android TWA

  // iOS doesn't support beforeinstallprompt event
  // So we can only show manual install instructions for iOS
  const canInstallPWA = !isStandalone && ('BeforeInstallPromptEvent' in window || isIOS);

  return {
    isMobile,
    isIOS,
    isAndroid,
    isStandalone,
    canInstallPWA,
    isIOSSafari,
    isSamsungBrowser
  };
};

/**
 * Check if device has touchscreen capability
 * @version 6.3.10
 */
const hasTouchScreen = (): boolean => {
  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    (navigator as any).msMaxTouchPoints > 0
  );
};

/**
 * Check if viewport size indicates mobile device
 * Standard mobile breakpoint: 768px (matches Tailwind md: breakpoint)
 * @version 6.3.10
 */
const isMobileViewport = (): boolean => {
  return window.innerWidth <= 768;
};

/**
 * Enhanced mobile detection with multiple fallbacks
 *
 * Detection layers:
 * 1. Primary: User-Agent based detection (catches 95% of cases)
 * 2. Fallback: iPad with iPadOS 13+ (reports as Mac but has touch)
 * 3. Fallback: Any touch device with mobile viewport (catches "Request Desktop Site")
 * 4. Fallback: Installed PWA with mobile viewport
 *
 * @version 6.3.10 - Added multi-layer detection for edge cases
 */
export const isMobileDevice = (): boolean => {
  const deviceInfo = getDeviceInfo();

  // 1. Primary: User-Agent based detection (original logic)
  if (deviceInfo.isMobile) return true;

  // 2. Fallback: iPad with iPadOS 13+ (reports as Mac but has touch)
  // iPads on iOS 13+ report UA as "Macintosh" instead of "iPad" in Safari
  const isProbablyIPad =
    /macintosh/i.test(navigator.userAgent) &&
    hasTouchScreen() &&
    isMobileViewport();
  if (isProbablyIPad) return true;

  // 3. Fallback: Any touch device with mobile viewport
  // Catches mobile browsers with "Request Desktop Site" enabled
  const isTouchMobile = hasTouchScreen() && isMobileViewport();
  if (isTouchMobile) return true;

  // 4. Fallback: Check standalone mode (installed PWA on mobile)
  // PWA might have weird UA but if installed on mobile viewport, it's mobile
  if (deviceInfo.isStandalone && isMobileViewport()) return true;

  return false;
};

/**
 * Check if device is iOS
 */
export const isIOSDevice = (): boolean => {
  return getDeviceInfo().isIOS;
};

/**
 * Check if device is Android
 */
export const isAndroidDevice = (): boolean => {
  return getDeviceInfo().isAndroid;
};

/**
 * Check if app is running as installed PWA
 */
export const isInstalledPWA = (): boolean => {
  return getDeviceInfo().isStandalone;
};

/**
 * Check if PWA can be installed (shows install prompt)
 */
export const canInstallPWA = (): boolean => {
  return getDeviceInfo().canInstallPWA;
};

/**
 * Check if running in iOS Safari (for manual install instructions)
 */
export const isIOSSafari = (): boolean => {
  return getDeviceInfo().isIOSSafari;
};

/**
 * Get display mode (standalone, browser, etc)
 */
export const getDisplayMode = (): string => {
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return 'standalone';
  }
  if (window.matchMedia('(display-mode: fullscreen)').matches) {
    return 'fullscreen';
  }
  if (window.matchMedia('(display-mode: minimal-ui)').matches) {
    return 'minimal-ui';
  }
  return 'browser';
};

/**
 * Map de token en User-Agent → nombre legible de la app.
 * Chequeados contra el UA raw (case-sensitive).
 * Orden: tokens más específicos primero (FBAN/FBAV antes que genéricos).
 */
const IN_APP_BROWSER_TOKENS: Record<string, string> = {
  'FBAN': 'Facebook',
  'FBAV': 'Facebook',
  'Instagram': 'Instagram',
  'Twitter': 'Twitter',
  'WhatsApp': 'WhatsApp',
  'Line': 'Line',
  'Snapchat': 'Snapchat',
  'GSA': 'Google Search',
};

/**
 * Detecta si el contexto actual es un in-app browser (WebView dentro de
 * una app social/messaging). Funciona en Android e iOS.
 *
 * Android: markers `wv)` o `WebView` en el UA.
 * iOS: apps como WhatsApp/Instagram inyectan su nombre en el UA.
 *       Fallback: tiene AppleWebKit pero NO "Mobile Safari" → WebView.
 * Tokens sociales chequeados en ambas plataformas (case-sensitive).
 */
export const isInAppBrowser = (): boolean => {
  const ua = navigator.userAgent;
  const uaLower = ua.toLowerCase();

  // Android WebView markers
  if (/android/i.test(ua)) {
    if (uaLower.includes('wv)') || ua.includes('WebView')) {
      return true;
    }
  }

  // Tokens de apps sociales (case-sensitive, ambas plataformas)
  for (const token of Object.keys(IN_APP_BROWSER_TOKENS)) {
    if (ua.includes(token)) {
      return true;
    }
  }

  // iOS fallback: AppleWebKit presente pero sin "Safari/" → WebView
  // El UA de Safari real contiene "Safari/604.1"; los WebViews NO tienen "Safari/"
  if (/iphone|ipad|ipod/i.test(ua)) {
    if (!ua.includes('Safari/') && ua.includes('AppleWebKit')) {
      return true;
    }
  }

  return false;
};

/**
 * Retorna nombre legible del in-app browser detectado, o null si no es in-app.
 * Útil para mostrar "Abriste desde WhatsApp" en la UI.
 */
export const getInAppBrowserName = (): string | null => {
  if (!isInAppBrowser()) return null;

  const ua = navigator.userAgent;
  for (const [token, name] of Object.entries(IN_APP_BROWSER_TOKENS)) {
    if (ua.includes(token)) {
      return name;
    }
  }

  return null;
};
